import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Award, MessageCircle, Loader2, Send, X, Sparkles, Image as ImageIcon, Users, Zap } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Input } from '@/features/shared/components/ui/input';
import { VoiceInputButton } from '@/features/shared/components/VoiceInputButton';
import { getApiErrorMessage } from '@/lib/apiError';
import { useHasPermission } from '@/rbac/useRbac';
import * as rec from '@/services/recognitionService';
import * as workforce from '@/services/workforceService';
import { inFlowRate, inFlowIsWorkOnly, sourceBuckets, aiAssistRate, pct } from './recognitionInsights';
// One recognition composer in the product — Conversations opens the same one.
import {
  Avatar,
  GiveRecognitionComposer as Composer,
  recipientLabel,
  type ComposerInitial,
} from '@/features/performance/GiveRecognitionComposer';

// Recognition Community feed (Phase 1): the tenant-wide feed with applaud/celebrate
// reactions and comments, plus an explicit "Give recognition" composer — the
// discoverable home for recognition (previously buried in work-item threads).

const REACTIONS: { kind: string; emoji: string; label: string }[] = [
  { kind: 'applaud', emoji: '👏', label: 'Applaud' },
  { kind: 'celebrate', emoji: '🎉', label: 'Celebrate' },
  { kind: 'support', emoji: '💪', label: 'Support' },
  { kind: 'love', emoji: '❤️', label: 'Love' },
];


const timeAgo = (iso: string) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

// ---- one feed card ----------------------------------------------------------
const FeedCard: React.FC<{ item: rec.FeedItem }> = ({ item }) => {
  const { t } = useTranslation('performance');
  const [reactions, setReactions] = useState<Record<string, number>>(item.reactions || {});
  const [mine, setMine] = useState<string[]>(item.my_reactions || []);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState<rec.RecognitionComment[]>(item.recent_comments || []);
  const [commentCount, setCommentCount] = useState(item.comment_count || 0);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  const react = async (kind: string) => {
    try {
      const r = await rec.reactToRecognition(item.id, kind);
      setReactions(r.reactions || {});
      setMine((prev) => (r.on ? [...prev, kind] : prev.filter((k) => k !== kind)));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('err', 'Something went wrong')));
    }
  };

  const openComments = async () => {
    const next = !showComments;
    setShowComments(next);
    if (next) {
      try {
        const all = await rec.listRecognitionComments(item.id);
        setComments(all);
        setCommentCount(all.length);
      } catch { /* keep the recent ones */ }
    }
  };

  const postComment = async () => {
    const body = draft.trim();
    if (!body) return;
    setBusy(true);
    try {
      const c = await rec.addRecognitionComment(item.id, body);
      setComments((prev) => [...prev, c]);
      setCommentCount((n) => n + 1);
      setDraft('');
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('err', 'Something went wrong')));
    } finally {
      setBusy(false);
    }
  };

  const totalReactions = Object.values(reactions).reduce((a, b) => a + b, 0);

  return (
    <Card>
      <CardContent className="pt-4">
        <div className="flex gap-3">
          <Avatar name={item.recognizer_name || 'Someone'} seed={item.recognizer_user_id} />
          <div className="flex-1 min-w-0">
            <div className="text-sm leading-snug">
              <span className="font-semibold text-foreground">{item.recognizer_name || t('someone', 'Someone')}</span>
              <span className="text-muted-foreground"> {t('recognized', 'recognized')} </span>
              <span className="font-semibold text-foreground">{recipientLabel(item, t as unknown as (k: string, d?: string) => string)}</span>
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{timeAgo(item.created_at)}</div>
            {(item.category_label || item.category_key) && (
              <span className="inline-flex items-center gap-1 mt-2 text-[11px] font-medium px-2.5 py-1 rounded-full bg-accent text-accent-foreground">
                {item.category_label || item.category_key.replace(/_/g, ' ')}
              </span>
            )}
            {item.tags && item.tags.length > 0 && (
              <div className="mt-2 text-[11px] text-muted-foreground">
                🔖 {t('tagged', 'Tagged')}: {item.tags.map((p) => p.name).filter(Boolean).join(', ')}
              </div>
            )}
            {item.title && <div className="mt-2 text-sm font-medium text-foreground">{item.title}</div>}
            {item.message && <p className="mt-1 text-sm text-foreground/90">{item.message}</p>}
            {item.media_url && (
              <div className="mt-3 rounded-xl overflow-hidden border border-border">
                <img src={item.media_url} alt="" loading="lazy" className="w-full max-h-96 object-cover" />
              </div>
            )}
            {item.source_domain === 'work' && (
              <span className="inline-flex items-center gap-1.5 mt-2 text-[11px] text-muted-foreground bg-muted px-2 py-1 rounded-md">
                🔗 {t('from_work', 'From work')}
              </span>
            )}

            {/* reactions */}
            <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-border flex-wrap">
              {REACTIONS.map((r) => {
                const on = mine.includes(r.kind);
                const count = reactions[r.kind] || 0;
                return (
                  <button
                    key={r.kind}
                    type="button"
                    onClick={() => react(r.kind)}
                    title={r.label}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                      on ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    <span>{r.emoji}</span>{count > 0 && <span>{count}</span>}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={openComments}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-muted-foreground hover:bg-muted"
              >
                <MessageCircle className="w-3.5 h-3.5" /> {commentCount > 0 ? commentCount : t('comment', 'Comment')}
              </button>
              {totalReactions > 0 && (
                <span className="ml-auto text-[11px] text-muted-foreground">
                  {totalReactions} {totalReactions === 1 ? t('reaction', 'reaction') : t('reactions', 'reactions')}
                </span>
              )}
            </div>

            {/* comments */}
            {showComments && (
              <div className="mt-3 flex flex-col gap-2.5">
                {comments.map((c) => (
                  <div key={c.id} className="flex gap-2">
                    <Avatar name={c.author_name || '?'} seed={c.user_id} size={26} />
                    <div className="bg-muted rounded-xl px-3 py-1.5 text-xs">
                      <div className="font-semibold text-foreground text-[11px]">{c.author_name || t('someone', 'Someone')}</div>
                      {c.body}
                    </div>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <Input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && postComment()}
                    placeholder={t('add_comment', 'Add a comment…')}
                    className="h-9 text-sm"
                  />
                  <Button size="sm" onClick={postComment} disabled={busy || !draft.trim()}>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// ---- composer ---------------------------------------------------------------
// "recognized A", "A & B", or "A, B & C" from the recipient set (falls back to
// the single primary recipient name for older/single-recipient posts).


// ---- Insights (recognition:analytics) ---------------------------------------
const StatCard: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode; hint?: string }> = ({ icon, label, value, hint }) => (
  <Card><CardContent className="pt-4">
    <div className="flex items-center gap-2 text-muted-foreground text-xs font-medium uppercase tracking-wide">{icon}{label}</div>
    <div className="mt-1 text-2xl font-bold tabular-nums">{value}</div>
    {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
  </CardContent></Card>
);

const Leaderboard: React.FC<{ title: string; people: rec.RecognitionPersonCount[]; empty: string }> = ({ title, people, empty }) => (
  <Card><CardContent className="pt-4">
    <div className="text-sm font-semibold mb-3">{title}</div>
    {people.length === 0 ? (
      <div className="text-[13px] text-muted-foreground py-2">{empty}</div>
    ) : (
      <div className="flex flex-col gap-2.5">
        {people.map((p, i) => (
          <div key={p.user_id} className="flex items-center gap-3">
            <span className="w-5 text-center text-xs font-semibold text-muted-foreground tabular-nums">{i + 1}</span>
            <Avatar name={p.name || '?'} seed={p.user_id} size={30} />
            <span className="flex-1 text-sm truncate" title={p.name}>{p.name || '—'}</span>
            <span className="text-sm font-semibold tabular-nums">{p.count}</span>
          </div>
        ))}
      </div>
    )}
  </CardContent></Card>
);

const TrendBars: React.FC<{ series: rec.RecognitionWeekBucket[]; title: string }> = ({ series, title }) => {
  const max = Math.max(1, ...series.map((w) => w.count));
  return (
    <Card><CardContent className="pt-4">
      <div className="text-sm font-semibold mb-3">{title}</div>
      <div className="flex items-end gap-1.5 h-28">
        {series.map((w) => (
          <div key={w.week_start} className="flex-1 flex flex-col items-center gap-1 group">
            <div className="w-full text-center text-[10px] text-muted-foreground tabular-nums opacity-0 group-hover:opacity-100 transition-opacity">{w.count}</div>
            <div
              className="w-full rounded-t bg-orange-500/80 hover:bg-orange-500 transition-colors"
              style={{ height: `${Math.max(3, (w.count / max) * 88)}px` }}
              title={`${new Date(w.week_start).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}: ${w.count}`}
            />
            <div className="text-[9px] text-muted-foreground tabular-nums">{new Date(w.week_start).toLocaleDateString(undefined, { month: 'numeric', day: 'numeric' })}</div>
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
};

// Where recognition is given from. In-flow sources are highlighted, because the
// point of the panel is whether recognition is happening at the work or being
// composed from scratch afterwards.
const SourceBreakdown: React.FC<{ sources: rec.RecognitionSourceCount[]; title: string }> = ({ sources, title }) => {
  if (sources.length === 0) return null;
  const max = Math.max(1, ...sources.map((s) => s.count));
  return (
    <Card><CardContent className="pt-4">
      <div className="text-sm font-semibold mb-3">{title}</div>
      <div className="flex flex-col gap-2.5">
        {sources.map((s) => (
          <div key={s.key} className="flex items-center gap-3">
            <span className="w-32 text-[13px] truncate" title={s.label}>{s.label}</span>
            <div className="flex-1 h-2.5 rounded-full bg-muted overflow-hidden">
              <div
                className={`h-full rounded-full ${s.in_flow ? 'bg-emerald-500/80' : 'bg-muted-foreground/40'}`}
                style={{ width: `${(s.count / max) * 100}%` }}
              />
            </div>
            <span className="text-sm font-semibold tabular-nums w-8 text-right">{s.count}</span>
          </div>
        ))}
      </div>
    </CardContent></Card>
  );
};

const RecognitionInsights: React.FC = () => {
  const { t } = useTranslation('performance');
  const [data, setData] = useState<rec.RecognitionAnalytics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    rec.getRecognitionAnalytics().then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="py-16 grid place-items-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (!data || data.total === 0) {
    return <Card><CardContent className="py-12 text-center text-muted-foreground text-sm">
      {t('insights_empty', 'No recognition activity yet. Insights appear once colleagues start recognizing each other.')}
    </CardContent></Card>;
  }

  const inFlowPct = pct(inFlowRate(data));
  const workOnly = inFlowIsWorkOnly(data);
  const sources = sourceBuckets(data);
  const aiRate = aiAssistRate(data);
  const topCats = (data.by_category_labeled || []).slice(0, 6);
  const maxCat = Math.max(1, ...topCats.map((c) => c.count));

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={<Award className="w-3.5 h-3.5" />} label={t('stat_total', 'Recognitions')} value={data.total} />
        <StatCard icon={<Users className="w-3.5 h-3.5" />} label={t('stat_recipients', 'People recognized')} value={data.distinct_recipients} />
        <StatCard icon={<Sparkles className="w-3.5 h-3.5" />} label={t('stat_givers', 'People recognizing')} value={data.distinct_recognizers} />
        <StatCard
          icon={<Zap className="w-3.5 h-3.5" />}
          label={t('stat_inflow', 'In the flow of work')}
          value={`${inFlowPct}%`}
          // An older backend can only tell us about work items. Say which
          // number this is rather than quietly under-reporting conversations.
          hint={workOnly
            ? t('stat_inflow_hint_work', 'given on work items')
            : t('stat_inflow_hint', 'given where the work happened')}
        />
      </div>
      {aiRate !== null && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            icon={<Sparkles className="w-3.5 h-3.5" />}
            label={t('stat_ai_assist', 'Drafted with help')}
            value={`${pct(aiRate)}%`}
            hint={t('stat_ai_assist_hint', 'a person confirmed every one')}
          />
        </div>
      )}
      <SourceBreakdown sources={sources} title={t('by_source', 'Where recognition comes from')} />

      <TrendBars series={data.weekly_trend || []} title={t('trend_title', 'Recognitions per week')} />

      <div className="grid md:grid-cols-2 gap-4">
        <Leaderboard title={t('top_recipients', 'Most recognized')} people={data.top_recipients || []} empty={t('none_yet', 'None yet')} />
        <Leaderboard title={t('top_recognizers', 'Top recognizers')} people={data.top_recognizers || []} empty={t('none_yet', 'None yet')} />
      </div>

      {topCats.length > 0 && (
        <Card><CardContent className="pt-4">
          <div className="text-sm font-semibold mb-3">{t('by_category', 'By category')}</div>
          <div className="flex flex-col gap-2.5">
            {topCats.map((c) => (
              <div key={c.key} className="flex items-center gap-3">
                <span className="w-32 text-[13px] truncate" title={c.label}>{c.label}</span>
                <div className="flex-1 h-2.5 rounded-full bg-muted overflow-hidden">
                  <div className="h-full rounded-full bg-orange-500/80" style={{ width: `${(c.count / maxCat) * 100}%` }} />
                </div>
                <span className="text-sm font-semibold tabular-nums w-8 text-right">{c.count}</span>
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}
    </div>
  );
};

// ---- the tab ----------------------------------------------------------------
const RecognitionFeed: React.FC = () => {
  const { t } = useTranslation('performance');
  const canSeeAnalytics = useHasPermission('recognition:analytics');
  const [view, setView] = useState<'feed' | 'team' | 'following' | 'received' | 'given' | 'insights'>('feed');
  const [suggestions, setSuggestions] = useState<rec.FollowPerson[]>([]);
  const [feed, setFeed] = useState<rec.FeedItem[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [list, setList] = useState<rec.Recognition[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [compose, setCompose] = useState(false);
  const [composeInitial, setComposeInitial] = useState<ComposerInitial | undefined>(undefined);
  const [celebrations, setCelebrations] = useState<rec.Celebration[]>([]);
  const [stats, setStats] = useState<rec.RecognitionAnalytics | null>(null);

  const openCompose = (initial?: ComposerInitial) => { setComposeInitial(initial); setCompose(true); };

  const feedScope: 'team' | 'following' | undefined =
    view === 'team' ? 'team' : view === 'following' ? 'following' : undefined;

  const loadFeed = (reset = true) => {
    if (reset) { setLoading(true); }
    rec.getRecognitionFeed(reset ? undefined : cursor, 20, feedScope)
      .then((p) => {
        setFeed((prev) => (reset ? p.items : [...prev, ...p.items]));
        setCursor(p.next_cursor);
      })
      .catch(() => { if (reset) setFeed([]); })
      .finally(() => { setLoading(false); setLoadingMore(false); });
  };

  const loadList = (v: 'received' | 'given') => {
    setLoading(true);
    (v === 'received' ? rec.getRecognitionReceived() : rec.getRecognitionGiven())
      .then(setList).catch(() => setList([])).finally(() => setLoading(false));
  };

  const loadSuggestions = () => {
    rec.getPeopleSuggestions().then(setSuggestions).catch(() => setSuggestions([]));
  };

  useEffect(() => {
    if (view === 'feed' || view === 'team' || view === 'following') {
      loadFeed(true);
      rec.getCelebrations().then(setCelebrations).catch(() => setCelebrations([]));
      loadSuggestions();
      // Stat strip (HR/managers only — reuses the gated analytics summary).
      if (canSeeAnalytics && !stats) {
        rec.getRecognitionAnalytics().then(setStats).catch(() => setStats(null));
      }
    } else if (view === 'received' || view === 'given') {
      loadList(view);
    }
    // 'insights' loads its own data inside RecognitionInsights.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const toggleFollow = (p: rec.FollowPerson) => {
    // optimistic
    setSuggestions((prev) => prev.map((x) => x.user_id === p.user_id ? { ...x, following: !x.following } : x));
    const call = p.following ? rec.unfollowPerson(p.user_id) : rec.followPerson(p.user_id);
    call.catch(() => {
      setSuggestions((prev) => prev.map((x) => x.user_id === p.user_id ? { ...x, following: p.following } : x));
      toast.error(t('follow_error', 'Could not update follow. Try again.'));
    });
  };

  const congratulate = (c: rec.Celebration) => {
    const name = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
    const who = name || t('your_colleague', 'your colleague');
    const isBirthday = c.kind === 'birthday';
    openCompose({
      recipient: c.user_id ? { id: c.employee_id, user_id: c.user_id, name } : undefined,
      title: isBirthday
        ? t('bday_title', 'Happy birthday, {{name}}! 🎂', { name: who })
        : c.years ? t('anniv_title', 'Happy {{n}}-year work anniversary!', { n: c.years }) : t('congrats', 'Congratulations!'),
      message: isBirthday
        ? t('bday_msg', 'Wishing you a wonderful birthday, {{name}}! 🎉', { name: who })
        : c.years ? t('anniv_msg', 'Thank you for {{n}} years with the team, {{name}} — here’s to many more. 🎉', { n: c.years, name: who }) : '',
    });
  };

  const tabs: { k: typeof view; label: string }[] = [
    { k: 'feed', label: t('tab_feed', 'Feed') },
    { k: 'team', label: t('tab_team', 'Team') },
    { k: 'following', label: t('tab_following', 'Following') },
    { k: 'received', label: t('rec_received', 'Received') },
    { k: 'given', label: t('rec_given', 'Given') },
    ...(canSeeAnalytics ? [{ k: 'insights' as typeof view, label: t('tab_insights', 'Insights') }] : []),
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="inline-flex bg-muted p-1 rounded-xl">
          {tabs.map((tb) => (
            <button
              key={tb.k}
              type="button"
              onClick={() => setView(tb.k)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                view === tb.k ? 'bg-card text-orange-600 shadow-sm' : 'text-muted-foreground'
              }`}
            >
              {tb.label}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <Button onClick={() => openCompose()}><Award className="w-4 h-4 mr-1.5" /> {t('give_recognition', 'Give recognition')}</Button>
      </div>

      {view === 'insights' ? (
        <RecognitionInsights />
      ) : loading ? (
        <div className="py-16 grid place-items-center text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>
      ) : (view === 'feed' || view === 'team' || view === 'following') ? (
        <div className="grid lg:grid-cols-[minmax(0,1fr)_300px] gap-6 items-start">
          {/* ---- main column ---- */}
          <div className="flex flex-col gap-4 min-w-0">
            {/* stat strip (HR/managers) */}
            {stats && stats.total > 0 && (
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-card border border-border rounded-2xl px-4 py-3 shadow-sm">
                  <div className="text-xl font-bold tabular-nums text-orange-600">{stats.total}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{t('stat_total_all', 'Recognitions all-time')}</div>
                </div>
                <div className="bg-card border border-border rounded-2xl px-4 py-3 shadow-sm">
                  <div className="text-xl font-bold tabular-nums text-orange-600">{stats.distinct_recipients}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{t('stat_recipients', 'People recognized')}</div>
                </div>
                <div className="bg-card border border-border rounded-2xl px-4 py-3 shadow-sm">
                  <div className="text-xl font-bold tabular-nums text-orange-600">{celebrations.length}</div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">{t('stat_celebrations', 'Upcoming celebrations')}</div>
                </div>
              </div>
            )}
            {/* celebrations — horizontal strip on mobile only (rail carries them on desktop) */}
            {celebrations.length > 0 && (
              <div className="lg:hidden">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">🎉 {t('celebrations', 'Celebrations')}</div>
                <div className="flex gap-3 overflow-x-auto pb-1">
                  {celebrations.map((c) => {
                    const name = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
                    return (
                      <div key={c.employee_id} className="shrink-0 w-40 bg-card border border-border rounded-2xl p-3 text-center shadow-sm">
                        <div className="text-2xl">{c.kind === 'birthday' ? '🎂' : '🎊'}</div>
                        <div className="text-sm font-semibold mt-1 truncate" title={name}>{name}</div>
                        <div className="text-[11px] text-muted-foreground">{c.kind === 'birthday' ? t('birthday', 'Birthday') : `${c.years} ${c.years === 1 ? t('year', 'yr') : t('years', 'yrs')}`} · {new Date(c.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</div>
                        <button onClick={() => congratulate(c)} className="mt-2 w-full py-1.5 rounded-lg text-[11px] font-semibold bg-accent text-accent-foreground hover:bg-orange-500 hover:text-white transition-colors">{t('congratulate', 'Congratulate')}</button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {feed.length === 0 ? (
              <Card><CardContent className="py-12 text-center text-muted-foreground text-sm">
                {view === 'team'
                  ? t('team_feed_empty', 'No recognitions involving your team yet.')
                  : view === 'following'
                  ? t('following_feed_empty', 'Follow colleagues to see their recognitions here. Try the “People to celebrate” panel.')
                  : t('feed_empty', 'No recognitions yet. Be the first — recognize a colleague!')}
              </CardContent></Card>
            ) : (
              <>
                {feed.map((it) => <FeedCard key={it.id} item={it} />)}
                {cursor && (
                  <Button variant="outline" onClick={() => { setLoadingMore(true); loadFeed(false); }} disabled={loadingMore} className="self-center">
                    {loadingMore ? <Loader2 className="w-4 h-4 animate-spin" /> : t('load_more', 'Load more')}
                  </Button>
                )}
              </>
            )}
          </div>

          {/* ---- right rail (desktop) ---- */}
          <aside className="hidden lg:flex flex-col gap-4 sticky top-4">
            <Card><CardContent className="pt-4">
              <div className="text-sm font-semibold mb-3">🎉 {t('celebrations', 'Celebrations')}</div>
              {celebrations.length === 0 ? (
                <p className="text-[13px] text-muted-foreground">
                  {t('celebrations_empty', 'No work anniversaries or birthdays in the next 30 days. Birthdays show here for colleagues who opt in on their profile.')}
                </p>
              ) : (
                <div className="flex flex-col gap-3">
                  {celebrations.slice(0, 5).map((c) => {
                    const name = [c.first_name, c.last_name].filter(Boolean).join(' ').trim();
                    const sub = c.kind === 'birthday'
                      ? `${t('birthday', 'Birthday')} · ${new Date(c.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`
                      : `${c.years} ${c.years === 1 ? t('year', 'yr') : t('years', 'yrs')} · ${new Date(c.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
                    return (
                      <div key={c.employee_id} className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full grid place-items-center text-sm shrink-0" style={{ background: c.kind === 'birthday' ? '#F59E0B22' : '#05966922' }}>{c.kind === 'birthday' ? '🎂' : '🎊'}</div>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px] font-semibold truncate" title={name}>{name || '—'}</div>
                          <div className="text-[11px] text-muted-foreground truncate">{sub}</div>
                        </div>
                        <button onClick={() => congratulate(c)} className="text-[11px] font-semibold text-orange-600 hover:text-orange-500 shrink-0">
                          {c.kind === 'birthday' ? t('celebrate', 'Celebrate') : t('congratulate', 'Congratulate')}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent></Card>

            <Card><CardContent className="pt-4">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <span className="w-6 h-6 rounded-lg grid place-items-center text-white text-xs" style={{ background: 'linear-gradient(135deg,#F28A3A,#F6B26B)' }}>✦</span>
                {t('dana_can_help', 'Dana can help')}
              </div>
              <p className="text-[13px] text-muted-foreground mt-2 mb-3">
                {t('dana_rail_blurb', 'Not sure what to say? Dana drafts a warm, specific recognition from a name and a moment — you tweak & post.')}
              </p>
              <Button variant="outline" className="w-full" onClick={() => openCompose()}>
                <Sparkles className="w-4 h-4 mr-1.5" /> {t('draft_with_dana', 'Draft with Dana')}
              </Button>
            </CardContent></Card>

            {suggestions.length > 0 ? (
              <Card><CardContent className="pt-4">
                <div className="text-sm font-semibold mb-3">{t('people_to_celebrate', 'People to celebrate')}</div>
                <div className="flex flex-col gap-3">
                  {suggestions.map((p) => (
                    <div key={p.user_id} className="flex items-center gap-2.5">
                      <Avatar name={p.name || '?'} seed={p.user_id} size={30} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold truncate" title={p.name}>{p.name || '—'}</div>
                        {p.sublabel && <div className="text-[11px] text-muted-foreground truncate">{p.sublabel}</div>}
                      </div>
                      <button
                        onClick={() => toggleFollow(p)}
                        className={`text-[11px] font-semibold shrink-0 px-2 py-1 rounded-md border transition-colors ${
                          p.following ? 'border-border text-muted-foreground hover:bg-muted' : 'border-orange-500 text-orange-600 hover:bg-orange-50'
                        }`}
                      >
                        {p.following ? t('following_btn', 'Following') : t('follow_btn', 'Follow')}
                      </button>
                      <button
                        onClick={() => openCompose({ recipient: { id: '', user_id: p.user_id, name: p.name } })}
                        title={t('recognize', 'Recognize')}
                        className="text-orange-600 hover:text-orange-500 shrink-0"
                      >
                        <Award className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </CardContent></Card>
            ) : (
              <Card><CardContent className="pt-4">
                <div className="text-sm font-semibold mb-1">{t('recognize_someone', 'Recognize someone')}</div>
                <p className="text-[13px] text-muted-foreground mb-3">{t('recognize_prompt', 'Someone did great work this week? Say so — it takes 30 seconds.')}</p>
                <Button className="w-full" onClick={() => openCompose()}><Award className="w-4 h-4 mr-1.5" /> {t('give_recognition', 'Give recognition')}</Button>
              </CardContent></Card>
            )}
          </aside>
        </div>
      ) : list.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground text-sm">
          {view === 'received'
            ? t('no_rec_received', 'No recognition yet. It appears here as colleagues recognize your work.')
            : t('no_rec_given', 'You haven’t recognized anyone yet.')}
        </CardContent></Card>
      ) : (
        <div className="flex flex-col gap-3 max-w-2xl">
          {list.map((r) => (
            <Card key={r.id}><CardContent className="pt-4">
              <div className="text-sm">
                <span className="font-semibold">{view === 'received' ? t('from', 'From a colleague') : t('to', 'To a colleague')}</span>
                {r.title && <span> · {r.title}</span>}
              </div>
              {r.message && <p className="mt-1 text-sm text-muted-foreground">{r.message}</p>}
              <div className="mt-1.5 text-[11px] text-muted-foreground">{r.category_key.replace(/_/g, ' ')} · {timeAgo(r.created_at)}</div>
            </CardContent></Card>
          ))}
        </div>
      )}

      {compose && <Composer initial={composeInitial} onClose={() => setCompose(false)} onPosted={() => { setCompose(false); setView('feed'); loadFeed(true); }} />}
    </div>
  );
};

export default RecognitionFeed;
